import {
  ChangeDetectionStrategy,
  Component,
  computed,
  Inject,
  OnInit,
  signal,
} from '@angular/core';
import { ModalComponent } from '../modal/modal.component';
import { ModalFooterComponent } from '../modal-footer/modal-footer.component';
import { delay, of, tap } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { catchError, filter } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { userFeature } from '@state';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { CodeInputComponent } from '@shared/components/code-input/code-input.component';
import { VerificationService } from '@core/services/verification.service';
import { TranslatePipe } from '@core/pipes';
import { AsyncPipe } from '@angular/common';
import { Substitutions } from '@selfcare/cms';
import { VerificationStatus, VerificationType } from '@core/enums';
import { ButtonComponent } from '../button/button.component';
import { SvgComponent } from '../svg/svg.component';

@Component({
  selector: 'sc-verification-code-modal',
  imports: [
    ButtonComponent,
    ModalComponent,
    ModalFooterComponent,
    SvgComponent,
    FormsModule,
    CodeInputComponent,
    TranslatePipe,
    AsyncPipe,
  ],
  templateUrl: './verification-code-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VerificationCodeModalComponent implements OnInit {
  user = this.store.selectSignal(userFeature.selectCurrent);
  currentMsisdn = this.store.selectSignal(userFeature.selectSelectedMsisdnNumber);

  code = signal<string>('');
  verificationStatus = signal(VerificationStatus.Initial);

  buttonTitleKey = computed(() => {
    switch (this.verificationStatus()) {
      case VerificationStatus.Sending:
        return 'components.verificationModal.sending';
      case VerificationStatus.Verifying:
        return 'components.verificationModal.verifying';
      default:
        return 'components.verificationModal.verify';
    }
  });

  isStatusPending = computed(() =>
    [VerificationStatus.Sending, VerificationStatus.Verifying, VerificationStatus.Success].includes(
      this.verificationStatus()
    )
  );

  Substitutions = Substitutions;
  VerificationType = VerificationType;
  VerificationStatusType = VerificationStatus;

  constructor(
    private readonly store: Store,
    private readonly dialogRef: DialogRef<boolean>,
    private readonly verificationService: VerificationService,
    @Inject(DIALOG_DATA)
    public data: { code: number; verificationType: VerificationType }
  ) {}

  public ngOnInit() {
    this.verificationStatus.set(VerificationStatus.Sending);
    this.verificationService
      .sendVerificationCode(this.currentMsisdn(), this.data.verificationType)
      .pipe(
        tap(() => this.verificationStatus.set(VerificationStatus.Sent)),
        catchError(() => {
          this.verificationStatus.set(VerificationStatus.Error);
          return of();
        })
      )
      .subscribe();
  }

  public verify() {
    if (!this.code()) {
      return;
    }

    this.verificationStatus.set(VerificationStatus.Verifying);
    this.verificationService
      .verifySmsCode(this.code())
      .pipe(
        tap((isVerified) => {
          if (isVerified) {
            this.verificationStatus.set(VerificationStatus.Success);
          } else {
            this.verificationStatus.set(VerificationStatus.Error);
          }
        }),
        filter((isVerified) => isVerified),
        delay(1000),
        tap((isVerified) => this.dialogRef.close(isVerified))
      )
      .subscribe();
  }

  public close() {
    this.dialogRef.close();
  }

  public codeCompleted(code: string) {
    this.code.set(code);
  }
}
